import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  displayUnitOf,
  hasDeclaredAge,
  resolveAgeDeclared,
  type AgeGesture,
  type AgeRead,
  type ThemePreference,
  type UnitsPreference,
  monotonicNowMs,
} from "@heraion/shared";
import { useRetryOnReconnect, useThemePreferenceContext } from "@heraion/ui";
import { useProfileDoc, type UseProfileDoc } from "./useProfileDoc";

// Bridges Profile.themePreference (cross-device source of truth) into the
// shared ThemeContext once the user is signed in. Renders only its children:
// it fetches the profile doc (useProfileDoc's own effect) and forwards the
// themePreference field via syncRemote, which is a no-op once the user has
// picked something this session (see ThemeContext.tsx). Mounted inside
// App.tsx's signed_in branch so the profile fetch only happens post sign-in.
// #182: the same read also gives the shell the athlete's unit, so the bell
// does not open another GET /api/profiles for it. A unit saved in Preferences
// goes through this same store, so it shows here at once.
const UnitContext = createContext<UnitsPreference>(displayUnitOf(null));
export const useProfileUnit = (): UnitsPreference => useContext(UnitContext);

// #175: the age-declaration overlay reads this same doc instead of opening its
// own GET. A declaration POST or a #383 withdrawal DELETE that lands is marked
// here with its time (the twin of mobile's useAgeDeclared), and the shared
// resolver weighs it against the doc's read: a read that left earlier cannot
// undo it, a later one (another device's change) decides.
// #306/#343: the web's one profile store. Every screen reads and writes the
// doc through here, so a mount opens no GET of its own.
type ShellProfile = UseProfileDoc & {
  /** Reads again unless a read is already running: the shell's own triggers. */
  rereadIfIdle: () => Promise<void>;
  /** Undefined until the doc or a gesture here says. */
  ageDeclared: boolean | undefined;
  noteAgeDeclared: () => void;
  noteAgeWithdrawn: () => void;
};
const noStore = async (): Promise<void> => {
  throw new Error("no_profile_store");
};
const ShellProfileContext = createContext<ShellProfile>({
  doc: null,
  readStartedAt: null,
  loading: true,
  error: null,
  saveField: noStore,
  savePatch: noStore,
  refresh: async () => {},
  rereadIfIdle: async () => {},
  ageDeclared: undefined,
  noteAgeDeclared: () => {},
  noteAgeWithdrawn: () => {},
});

// A doc the API returned (it carries its id); a 404's empty doc says nothing.
const ageReadOf = (doc: UseProfileDoc["doc"], startedAt: number | null): AgeRead | null =>
  doc === null || startedAt === null || typeof doc["id"] !== "string"
    ? null
    : { declared: hasDeclaredAge(doc), startedAt };
export const useShellProfile = (): ShellProfile => useContext(ShellProfileContext);
/** The athlete's unit, or null until the profile is read (a 404's empty doc
 *  is read): the gate of every weight write (#343). useProfileUnit's kg
 *  default is for display only. */
export const unitOfDoc = (doc: UseProfileDoc["doc"]): UnitsPreference | null =>
  doc === null ? null : displayUnitOf(doc);
export const useProfileUnitOrNull = (): UnitsPreference | null => unitOfDoc(useShellProfile().doc);
/** useProfileUnitOrNull, held from when `open` turns true until it turns
 *  false (#343): an editor open while a re-read brings another unit (changed
 *  on another device) keeps the unit its typed numbers were written in. */
export function useProfileUnitHeldWhile(open: boolean): UnitsPreference | null {
  const live = useProfileUnitOrNull();
  const [held, setHeld] = useState<UnitsPreference | null>(null);
  if (open && held === null && live !== null) setHeld(live);
  if (!open && held !== null) setHeld(null);
  return open ? (held ?? live) : live;
}
/** The doc's canonical bodyweight, or null when it has none (or no doc). */
export const bodyweightOf = (doc: UseProfileDoc["doc"]): number | null => {
  const raw = doc?.["bodyweightKg"];
  return typeof raw === "number" ? raw : null;
};

/** The shell reads the profile again on each change of `routeKey` (not on
 *  mount: the store's own first read is running). */
export function useProfileRereadOn(routeKey: string): void {
  const { rereadIfIdle } = useShellProfile();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    void rereadIfIdle();
  }, [routeKey, rereadIfIdle]);
}

export function ThemeProfileBridge({ children }: { children?: ReactNode }) {
  const { doc, readStartedAt, loading, error, saveField, savePatch, refresh: readNow } =
    useProfileDoc();
  // One read in flight for the shell's triggers. An explicit refresh (after a
  // write) always reads: one already running may have left before the write.
  const inFlight = useRef<Promise<void> | null>(null);
  const refresh = useCallback(() => {
    const p = readNow();
    inFlight.current = p;
    void p.finally(() => {
      if (inFlight.current === p) inFlight.current = null;
    });
    return p;
  }, [readNow]);
  // The store's own first read is not in `inFlight`: `loading` stands for it.
  const firstReadRunning = useRef(loading);
  firstReadRunning.current = loading;
  const rereadIfIdle = useCallback(
    () => (firstReadRunning.current ? Promise.resolve() : (inFlight.current ?? refresh())),
    [refresh],
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onFocus = () => void rereadIfIdle();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [rereadIfIdle]);
  const [gesture, setGesture] = useState<AgeGesture | null>(null);
  const noteAgeDeclared = useCallback(() => setGesture({ declared: true, at: monotonicNowMs() }), []);
  const noteAgeWithdrawn = useCallback(() => setGesture({ declared: false, at: monotonicNowMs() }), []);
  const ageDeclared = resolveAgeDeclared(ageReadOf(doc, readStartedAt), gesture);
  // A first read that failed reads again when the network is back (#397).
  const retry = useRetryOnReconnect(doc === null && error !== null);
  useEffect(() => {
    if (retry > 0) void refresh();
  }, [retry, refresh]);
  // A re-read that fails keeps the doc it had: no screen turns red for it.
  const readError = doc === null ? error : null;
  const shellProfile = useMemo(
    () => ({
      doc,
      readStartedAt,
      loading,
      error: readError,
      saveField,
      savePatch,
      refresh,
      rereadIfIdle,
      ageDeclared,
      noteAgeDeclared,
      noteAgeWithdrawn,
    }),
    [
      doc,
      readStartedAt,
      loading,
      readError,
      saveField,
      savePatch,
      refresh,
      rereadIfIdle,
      ageDeclared,
      noteAgeDeclared,
      noteAgeWithdrawn,
    ],
  );
  const ctx = useThemePreferenceContext();
  const themePreference = doc?.["themePreference"];

  useEffect(() => {
    ctx?.syncRemote(themePreference as Partial<ThemePreference> | null | undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themePreference]);

  return (
    <UnitContext.Provider value={displayUnitOf(doc)}>
      <ShellProfileContext.Provider value={shellProfile}>{children}</ShellProfileContext.Provider>
    </UnitContext.Provider>
  );
}
